import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminListToolbar, {
    AdminListToolbarButton,
} from '../../ui/admin/AdminListToolbar';
import {
    ColDef,
    ICellRendererParams,
    SortChangedEvent,
} from 'ag-grid-community';
import AdminList, {
    NameCellRenderer,
    ArticleCountCellRenderer,
    ArticleViewCellRenderer,
} from '../../ui/admin/AdminList';
import { useLazyQuery } from '@apollo/react-hooks';
import { User } from '../../../shared/types';
import { agGridDateFormatter, Sorting } from '../../../utils/ag-grid.utils';
import { ResearchRole } from '../../../shared/global-enums';
import { useArticleFilter } from '../../providers/admin/ArticleFilterProvider';
import { USERS_PAGINATED } from '../../../gql/users';
import { usePrevious } from '../../../utils/utils';
import { useUser } from '../../providers/UserProvider';

const name = 'Users';
const apiName = name.toLowerCase();

const UserFilterType: any = {
    All: 'All',
    ...ResearchRole,
    Trial: 'Trial',
};

interface UserListQueryUser extends User {
    activeChannelSubscriptionsCount?: number;
}

interface UserListItem extends User {
    activeChannelSubscriptionsCount: number;
}

interface UserTrial extends UserListItem {
    trialPeriod: number;
}

interface UserListQueryData {
    getPaginatedUsers: {
        users: UserListQueryUser[];
        total: number;
    };
}

const UserList = () => {
    const { hasScope } = useUser();
    const isAdmin = [ResearchRole.Administrator].some(hasScope);
    const [getUsers, { loading, error, data }] =
        useLazyQuery<UserListQueryData>(USERS_PAGINATED, {
            fetchPolicy: 'network-only',
        });

    const users: UserListItem[] = (data?.getPaginatedUsers.users || []).map(
        (user) => ({
            ...user,
            activeChannelSubscriptionsCount:
                user.activeChannelSubscriptionsCount ?? 0,
        })
    );
    const articleFilter = useArticleFilter();

    const [userFilterKind, setUserFilterKind] = useState(UserFilterType.All);
    const [searchString, setSearchString] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const prevCurrentPage = usePrevious(currentPage);
    const [sorting, setSorting] = useState<Sorting>({ field: 'name' });

    const total = useMemo(
        () => data?.getPaginatedUsers.total || 0,
        [data, loading]
    );

    const callGetUsers = useCallback(
        () =>
            getUsers({
                variables: {
                    includeActiveSubscriptions: isAdmin,
                    params: {
                        type: userFilterKind,
                        searchQuery: searchString,
                        sortField: sorting.field,
                        sortDirection: sorting.direction,
                        limit: perPage,
                        offset: (currentPage - 1) * perPage,
                    },
                },
            }),
        [searchString, userFilterKind, perPage, currentPage, sorting, isAdmin]
    );

    useEffect(() => {
        if (prevCurrentPage === currentPage && currentPage !== 1) {
            return setCurrentPage(1);
        }
        callGetUsers();
    }, [searchString, userFilterKind, perPage, currentPage, sorting]);

    useEffect(() => {
        callGetUsers();
    }, [currentPage]);

    const filteredAndSortedUsers: UserTrial[] = users.map((x) => ({
        ...x,
        trialPeriod: getTrialExpiration(x),
    }));

    const toolbarFilterButtons: AdminListToolbarButton[] = Object.entries(
        UserFilterType
    ).map(([key]): AdminListToolbarButton => {
        return {
            selected: userFilterKind === UserFilterType[key],
            onClick: (): void => setUserFilterKind(UserFilterType[key]),
            label: UserFilterType[UserFilterType[key]],
        } as AdminListToolbarButton;
    });

    const isSorted = useCallback(
        (field: string) => {
            return sorting.field === field
                ? { sort: sorting.direction || 'desc' }
                : {};
        },
        [sorting]
    );

    const columnDefs: ColDef[] = [
        {
            headerName: 'Email',
            field: 'email',
            valueGetter: (params) => params.data.email || 'Anonymous',
            cellRenderer: (props: any): JSX.Element => (
                <NameCellRenderer {...props} apiName={apiName} />
            ),
            width: 300, // 384 by designs
            menuTabs: [],
            sortable: false,
        },
        {
            headerName: 'Name',
            field: 'name',
            width: 275,
            menuTabs: [],
            sortable: false,
        },
        {
            headerName: 'Company',
            field: 'company',
            width: 175,
            menuTabs: [],
            sortable: false,
        },
        {
            headerName: 'Role',
            field: 'role',
            width: 134, // 200 by designs
            menuTabs: [],
            sortable: false,
        },
        ...(isAdmin
            ? [
                  {
                      headerName: 'Active subscriptions',
                      field: 'activeChannelSubscriptionsCount',
                      width: 180,
                      menuTabs: [],
                      sortable: false,
                  },
              ]
            : []),
        {
            headerName: 'Last signed in',
            field: 'lastLogin',
            ...isSorted('lastLogin'),
            width: 150, // 200 by designs
            valueFormatter: agGridDateFormatter,
            menuTabs: [],
        },
        {
            headerName: 'Trial Period',
            field: 'trialPeriod',
            valueFormatter: ({ data }) =>
                getTrialExpirationLabel(data.trialPeriod),
            tooltipValueGetter: ({ data }) =>
                getTrialExpirationLabel(data.trialPeriod),
            width: 160,
            menuTabs: [],
            sortable: false,
        },
        {
            headerName: 'Articles',
            field: 'articleCount',
            cellRenderer: (props: ICellRendererParams) => (
                <ArticleCountCellRenderer
                    value={props.value}
                    filter={articleFilter}
                    context={props.context}
                    filterData={{
                        _id: props.data._id,
                        name: props.data.name,
                        type: 'contributor',
                    }}
                />
            ),
            width: 150, // 200 by designs
            menuTabs: [],
            sortable: false,
        },
        {
            headerName: 'Views',
            field: 'views',
            width: 160,
            cellRenderer: (props: any) => (
                <ArticleViewCellRenderer
                    {...props}
                    apiName={apiName}
                    filter={articleFilter}
                    linkTo=""
                />
            ),
            menuTabs: [],
            sortable: false,
        },
    ];

    return (
        <AdminList
            name={name}
            maxWidth={1684}
            items={filteredAndSortedUsers}
            loading={loading}
            error={error}
            columns={columnDefs}
            showAdd={false}
            toolbar={
                <AdminListToolbar
                    filterItems={toolbarFilterButtons}
                    searchString={searchString}
                    toolbarPlaceholder="Search users..."
                    onQueryStringChange={setSearchString}
                />
            }
            gridOptions={{
                onSortChanged: (e: SortChangedEvent) => {
                    const sortColumn = e.api
                        .getColumnState()
                        .find((c) => c.sort !== null);
                    if (sortColumn) {
                        setSorting({
                            field: sortColumn.colId as string,
                            direction: sortColumn.sort as 'asc' | 'desc',
                        });
                    } else {
                        setSorting({ field: 'lastLogin' });
                    }
                },
            }}
            itemsNumber={total}
            showCaption={!loading && Boolean(total)}
            overwritePagination
            overwriteCurrentPage={currentPage}
            overwritePageRange={perPage}
            onCurrentPageChange={(v) => setCurrentPage(v)}
            onPerPageChange={(v) => setPerPage(v)}
        />
    );
};

export function formatTimeRemaining(minutes: number, daysOnly = false) {
    const days = minutes / (24 * 60);
    if (daysOnly && days > 1) {
        return `${Math.ceil(days)} days`;
    }
    const intDays = Math.floor(days);
    const hours = minutes / 60 - intDays * 24;
    const intHours = Math.floor(hours);
    const intMinutes = Math.floor(minutes - intHours * 60 - intDays * 24 * 60);
    const dayLabel = intDays === 1 ? 'day' : 'days';
    const hourLabel = intHours === 1 ? 'hour' : 'hours';

    return [
        [intDays, dayLabel],
        [intHours, hourLabel],
        [intMinutes, 'min'],
    ]
        .filter(([num]) => !!num)
        .map((t) => t.join(' '))
        .join(' ');
}

function getTrialExpirationLabel(minLeft: number): string {
    switch (minLeft) {
        case -1:
            return '';
        case 1000000:
            return 'Expired';
        default:
            return formatTimeRemaining(minLeft, true);
    }
}

function getTrialExpiration(user: User): number {
    switch (user.trialMinLeft) {
        case undefined:
            return -1;
        case -1:
            return -1;
        case 0:
            return 1000000;
        default:
            return user.trialMinLeft;
    }
}

export default UserList;
